"use client"

import { useState } from "react"
import {
  ArrowLeft,
  FileText,
  Download,
  Eye,
  Calendar,
  Users,
  TrendingUp,
  Target,
  Award,
  BarChart3,
  Filter,
  Search,
  Clock,
  Star,
  Briefcase,
  Grid3X3,
  List,
} from "lucide-react"
import "./PreGeneratedReportsPage.css"
import { useNavigate } from "react-router-dom";
import BackButton from "../shared/backButton";

const PreGeneratedReportsPage = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filterCategory, setFilterCategory] = useState("All")
  const [filterType, setFilterType] = useState("All")
  const [viewMode, setViewMode] = useState("list") // 'cards' or 'list'

  const reportCategories = [
    "All",
    "Employee Reports",
    "Team Reports",
    "Performance",
    "Training",
    "Leadership",
    "Mobility",
  ]
  const reportTypes = ["All", "Summary", "Detailed", "Comparative", "Trend Analysis"]

  const preGeneratedReports = [
    {
      id: 1,
      title: "Top 20 High Performers",
      description: "Comprehensive analysis of top-performing employees across all departments",
      category: "Employee Reports",
      type: "Summary",
      lastGenerated: "2024-01-22",
      downloads: 45,
      views: 234,
      icon: Star,
      color: "primary",
      insights: ["Performance metrics", "Skill assessments", "Career progression"],
      estimatedTime: "5 min read",
    },
    {
      id: 2,
      title: "Team Performance Comparison",
      description: "Detailed comparison of team performance metrics and productivity indicators",
      category: "Team Reports",
      type: "Comparative",
      lastGenerated: "2024-01-21",
      downloads: 32,
      views: 189,
      icon: Users,
      color: "secondary",
      insights: ["Team efficiency", "Collaboration scores", "Goal achievement"],
      estimatedTime: "8 min read",
    },
    {
      id: 3,
      title: "Skills Gap Analysis",
      description: "Organization-wide analysis of skill gaps and training requirements",
      category: "Training",
      type: "Detailed",
      lastGenerated: "2024-01-20",
      downloads: 67,
      views: 312,
      icon: Target,
      color: "tertiary",
      insights: ["Critical skill gaps", "Training recommendations", "Budget estimates"],
      estimatedTime: "12 min read",
    },
    {
      id: 4,
      title: "Leadership Pipeline Report",
      description: "Analysis of leadership potential and succession planning across the organization",
      category: "Leadership",
      type: "Summary",
      lastGenerated: "2024-01-19",
      downloads: 28,
      views: 156,
      icon: Award,
      color: "quaternary",
      insights: ["Leadership readiness", "Succession planning", "Development paths"],
      estimatedTime: "6 min read",
    },
    {
      id: 5,
      title: "Internal Mobility Trends",
      description: "Comprehensive analysis of internal movement patterns and career progression",
      category: "Mobility",
      type: "Trend Analysis",
      lastGenerated: "2024-01-18",
      downloads: 41,
      views: 203,
      icon: TrendingUp,
      color: "primary",
      insights: ["Movement patterns", "Career paths", "Retention impact"],
      estimatedTime: "10 min read",
    },
    {
      id: 6,
      title: "Department Performance Dashboard",
      description: "Real-time performance metrics and KPIs for all departments",
      category: "Performance",
      type: "Summary",
      lastGenerated: "2024-01-22",
      downloads: 89,
      views: 445,
      icon: BarChart3,
      color: "secondary",
      insights: ["KPI tracking", "Performance trends", "Benchmark comparisons"],
      estimatedTime: "7 min read",
    },
    {
      id: 7,
      title: "Training Effectiveness Analysis",
      description: "Detailed analysis of training program effectiveness and ROI",
      category: "Training",
      type: "Detailed",
      lastGenerated: "2024-01-17",
      downloads: 35,
      views: 178,
      icon: Briefcase,
      color: "tertiary",
      insights: ["Training ROI", "Skill improvement", "Program effectiveness"],
      estimatedTime: "15 min read",
    },
    {
      id: 8,
      title: "Employee Satisfaction Survey Results",
      description: "Comprehensive analysis of employee satisfaction and engagement metrics",
      category: "Employee Reports",
      type: "Summary",
      lastGenerated: "2024-01-16",
      downloads: 52,
      views: 267,
      icon: Users,
      color: "quaternary",
      insights: ["Satisfaction scores", "Engagement levels", "Improvement areas"],
      estimatedTime: "9 min read",
    },
  ]

  const filteredReports = preGeneratedReports.filter((report) => {
    const matchesSearch =
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === "All" || report.category === filterCategory
    const matchesType = filterType === "All" || report.type === filterType

    return matchesSearch && matchesCategory && matchesType
  })

  const getColorClass = (color) => {
    return `pregenerated-report-card-${color}`
  }

  const handleDownload = (report, e) => {
    e.stopPropagation()
    console.log(`Downloading ${report.title}`)
  }

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleView = (report) => {
    onNavigate("/reports/detailed-pre-generated/:id", report)
  }

  const renderCardView = () => (
    <div className="pregenerated-reports-grid">
      {filteredReports.map((report) => {
        const IconComponent = report.icon
        return (
          <div
            key={report.id}
            className={`pregenerated-report-card ${getColorClass(report.color)}`}
            onClick={() => handleView(report)}
          >
            <div className="pregenerated-card-header">
              <div className="pregenerated-card-icon">
                <IconComponent size={24} />
              </div>
              <div className="pregenerated-card-actions">
                <button
                  className="pregenerated-action-btn"
                  onClick={(e) => handleDownload(report, e)}
                  title="Download Report"
                >
                  <Download size={16} />
                </button>
                <button className="pregenerated-action-btn" onClick={() => handleView(report)} title="View Report">
                  <Eye size={16} />
                </button>
              </div>
            </div>

            <div className="pregenerated-card-content">
              <h3>{report.title}</h3>
              <p>{report.description}</p>

              <div className="pregenerated-card-insights">
                <h4>Key Insights:</h4>
                <ul>
                  {report.insights.map((insight, index) => (
                    <li key={index}>{insight}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pregenerated-card-footer">
              <div className="pregenerated-card-meta">
                <div className="pregenerated-meta-item">
                  <Calendar size={14} />
                  <span>{new Date(report.lastGenerated).toLocaleDateString()}</span>
                </div>
                <div className="pregenerated-meta-item">
                  <Clock size={14} />
                  <span>{report.estimatedTime}</span>
                </div>
              </div>
              <div className="pregenerated-card-stats">
                <div className="pregenerated-stat">
                  <Download size={14} />
                  <span>{report.downloads}</span>
                </div>
                <div className="pregenerated-stat">
                  <Eye size={14} />
                  <span>{report.views}</span>
                </div>
              </div>
            </div>

            <div className="pregenerated-card-category" style={{right: "120px", top: "32px"}}>{report.category}</div>
          </div>
        )
      })}
    </div>
  )

  const renderListView = () => (
    <div className="pregenerated-reports-table">
      <div className="pregenerated-table-header">
        <div className="pregenerated-table-cell">Report</div>
        <div className="pregenerated-table-cell">Category</div>
        <div className="pregenerated-table-cell">Type</div>
        <div className="pregenerated-table-cell">Last Generated</div>
        <div className="pregenerated-table-cell">Downloads</div>
        <div className="pregenerated-table-cell">Views</div>
        <div className="pregenerated-table-cell">Actions</div>
      </div>
      {filteredReports.map((report) => {
        const IconComponent = report.icon
        return (
          <div key={report.id} className="pregenerated-table-row" onClick={() => handleView(report)}>
            <div className="pregenerated-table-cell pregenerated-report-info">
              <div className={`pregenerated-list-icon ${getColorClass(report.color)}`}>
                <IconComponent size={20} />
              </div>
              <div className="pregenerated-report-details">
                <h4>{report.title}</h4>
                <p>{report.description}</p>
                <span className="pregenerated-estimated-time">{report.estimatedTime}</span>
              </div>
            </div>
            <div className="pregenerated-table-cell">
              <span className="pregenerated-category-badge">{report.category}</span>
            </div>
            <div className="pregenerated-table-cell">
              <span className="pregenerated-type-badge">{report.type}</span>
            </div>
            <div className="pregenerated-table-cell">{new Date(report.lastGenerated).toLocaleDateString()}</div>
            <div className="pregenerated-table-cell">{report.downloads}</div>
            <div className="pregenerated-table-cell">{report.views}</div>
            <div className="pregenerated-table-cell">
              <div className="pregenerated-table-actions">
                <button
                  className="pregenerated-table-action-btn"
                  onClick={(e) => handleDownload(report, e)}
                  title="Download Report"
                >
                  <Download size={16} />
                </button>
                <button
                  className="pregenerated-table-action-btn"
                  onClick={() => handleView(report)}
                  title="View Report"
                >
                  <Eye size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="pregenerated-reports-page">
      <BackButton />

      <main className="pregenerated-reports-main">
        <div className="container">
          {/* Header Section */}
          <div className="pregenerated-reports-header">
            <div className="pregenerated-header-content">
              <div className="pregenerated-header-icon">
                <FileText size={40} />
              </div>
              <div className="pregenerated-header-text">
                <h1>Pre-Generated Reports</h1>
                <p>Ready-to-use analytical reports with comprehensive insights and data visualizations</p>
              </div>
            </div>
            <div className="pregenerated-header-stats">
              <div className="pregenerated-stat-item">
                <div className="pregenerated-stat-number">{filteredReports.length}</div>
                <div className="pregenerated-stat-label">Available Reports</div>
              </div>
              <div className="pregenerated-stat-item">
                <div className="pregenerated-stat-number">
                  {preGeneratedReports.reduce((sum, report) => sum + report.downloads, 0)}
                </div>
                <div className="pregenerated-stat-label">Total Downloads</div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="pregenerated-filters-section">
          <div className="pregenerated-filters-row">
            <div className="pregenerated-search-container" style={{marginTop: "20px"}}>
              <Search size={20} className="pregenerated-search-icon" />
              <input
                type="text"
                placeholder="Search reports by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pregenerated-search-input"
              />
            </div>
            
              <div className="pregenerated-filter-group">
                <Filter size={16} />
                <label>Category:</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="pregenerated-filter-select"
                >
                  {reportCategories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pregenerated-filter-group">
                <Filter size={16} />
                <label>Type:</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="pregenerated-filter-select"
                >
                  {reportTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pregenerated-view-toggle">
                <button
                  className={`pregenerated-view-btn ${viewMode === "cards" ? "active" : ""}`}
                  onClick={() => setViewMode("cards")}
                >
                  <Grid3X3 size={16} />
                  Cards
                </button>
                <button
                  className={`pregenerated-view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                >
                  <List size={16} />
                  List
                </button>
              </div>
            </div>
          </div>

          {/* Reports Content */}
          {viewMode === "cards" ? renderCardView() : renderListView()}
        </div>
      </main>
    </div>
  )
}

export default PreGeneratedReportsPage
